/**
 * Theme context for managing dark/light mode
 */

import type { ReactNode } from 'react';
import React, { createContext, useContext, useState } from 'react';
import { storage } from '../lib/storage';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = 'theme';

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  // Helper to apply theme to document
  const applyThemeToDocument = (newTheme: Theme) => {
    document.documentElement.setAttribute('data-theme', newTheme);
  };

  // Initialize from localStorage immediately and apply to document
  const [theme, setTheme] = useState<Theme>(() => {
    const savedTheme = storage.getString(THEME_STORAGE_KEY);
    const initialTheme = savedTheme === 'light' || savedTheme === 'dark' ? savedTheme : 'dark';
    applyThemeToDocument(initialTheme);
    return initialTheme;
  });

  const toggleTheme = () => {
    const newTheme: Theme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    storage.setString(THEME_STORAGE_KEY, newTheme);
    applyThemeToDocument(newTheme);
  };

  const value: ThemeContextType = {
    theme,
    toggleTheme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
